<template>
  <div class="rounded-lg border border-gray-200 bg-white p-4">
    <!-- flex-wrap is load-bearing on narrow screens: unwrapped, the title plus the
         two legend labels are wider than a phone, and this row pushed the whole
         page into horizontal overflow — every card on the tone trainer got clipped,
         not just the chart. -->
    <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-2">
      <p class="text-sm font-semibold text-gray-700">Tone shape</p>
      <div class="flex items-center gap-3 text-xs text-gray-500">
        <span class="inline-flex items-center gap-1"><span class="w-3 h-1 rounded bg-emerald-500 inline-block"></span> {{ referenceLabel }}</span>
        <span class="inline-flex items-center gap-1"><span class="w-3 h-1 rounded bg-amber-500 inline-block"></span> You</span>
      </div>
    </div>
    <svg :viewBox="`0 0 ${W} ${H}`" class="w-full" role="img" aria-label="Pitch contour comparison">
      <line :x1="0" :y1="H / 2" :x2="W" :y2="H / 2" stroke="#f3f4f6" stroke-width="1" />
      <polyline v-if="referencePath" :points="referencePath" fill="none" stroke="#10b981" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
      <polyline v-if="userPath" :points="userPath" fill="none" stroke="#f59e0b" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.9" />
    </svg>
    <p class="mt-1 text-xs text-gray-400">
      The line is the pitch of the voice over time — try to match the shape, not the height.
    </p>
  </div>
</template>

<script setup>
import { computed } from 'vue'

// Contours arrive as [{ t: seconds, semitones: n }] with semitones relative to
// each speaker's own median pitch, so the two curves are comparable regardless
// of voice register. Time is normalized per-curve (utterance lengths differ) —
// what's being compared is the tone's shape.
const props = defineProps({
  reference: { type: Array, default: null },
  user:      { type: Array, default: null },
  // Legend label for the reference curve. Was the hardcoded string "Southern
  // speaker", which was wrong twice over: every clip is TTS, so "speaker" claims
  // a human we don't have, and this component also renders Northern practice,
  // where "Southern" is simply the wrong dialect.
  referenceLabel: { type: String, default: 'Reference' },
})

const W = 400
const H = 120
const PAD = 6

// Display clamp: learners routinely overshoot (a ±15 st dive), and on a shared
// scale that compresses the reference's real-but-modest movement (a nặng fall
// of ~5 st) into an apparently flat line. Vietnamese tones live within about
// ±9 st of the median; anything beyond is clipped to the chart edge, keeping
// both shapes readable without lying about direction.
const CLIP_ST = 9
const clip = (s) => Math.max(-CLIP_ST, Math.min(CLIP_ST, s))

// One shared semitone scale for both curves so "flat vs falling" reads true.
const range = computed(() => {
  const all = [ ...(props.reference || []), ...(props.user || []) ].map(p => clip(p.semitones))
  if (!all.length) return { min: -6, max: 6 }
  const min = Math.min(...all, -2)
  const max = Math.max(...all, 2)
  return { min, max }
})

function toPath(points) {
  if (!points || points.length < 2) return null
  const t0 = points[0].t
  const span = points[points.length - 1].t - t0 || 1
  const { min, max } = range.value
  return points.map(p => {
    const x = PAD + ((p.t - t0) / span) * (W - 2 * PAD)
    const y = PAD + (1 - (clip(p.semitones) - min) / (max - min)) * (H - 2 * PAD)
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')
}

const referencePath = computed(() => toPath(props.reference))
const userPath = computed(() => toPath(props.user))
</script>
